import { useCallback, useRef } from 'react';

/**
 * useHelpTarget — mark a region of the UI as "askable about" without
 * adding any visible affordance. Three contracts:
 *
 *   1. Focus / mousedown inside the region tags it as the user's
 *      "last help target." When the help orb is opened (manually or
 *      via Cmd-/), it picks up the recent target and scopes itself.
 *
 *   2. Right-click anywhere inside the region (suppressed on text
 *      inputs / textareas so paste still works) dispatches the
 *      ``helpTarget:open`` event — HelpOrb listens for this and opens
 *      the panel scoped to the target's article or pre-fill question.
 *
 *   3. The "last target" decays after 30 seconds. Stale interactions
 *      don't accidentally hijack a manual orb open.
 *
 * Returns a *callback ref* — not a useRef result. This is what lets
 * the hook work inside conditionally-mounted modals: React invokes
 * the callback with the DOM node on every mount and with null on
 * unmount, so listener attachment tracks the real lifecycle.
 *
 * Usage:
 *
 *   const helpRef = useHelpTarget({
 *     slug: 'discovery-check-reason-codes',
 *     label: 'Reason code dropdown',
 *     question: 'What do the reason codes mean?',
 *   });
 *   return <div ref={helpRef}>...</div>;
 *
 * Prefer wrapping the *container* (row, modal section) rather than
 * the individual input — right-clicks on inputs preserve native menu
 * via the text-input opt-out below.
 */

const STALENESS_MS = 30_000;
let _last = null;

export function getLastHelpTarget() {
  if (!_last) return null;
  if (Date.now() - _last.at > STALENESS_MS) return null;
  return _last;
}

export function clearLastHelpTarget() {
  _last = null;
}

function isTextEditable(el) {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const t = (el.type || '').toLowerCase();
    return t === '' || t === 'text' || t === 'search' || t === 'email' || t === 'url' || t === 'tel' || t === 'password';
  }
  return false;
}

export default function useHelpTarget({ slug, label, question, suppressContextMenu = true } = {}) {
  // Live target — read by the listener closures so we don't need to
  // re-attach listeners when opts change between renders.
  const targetRef = useRef({ slug, label, question, suppressContextMenu });
  targetRef.current = { slug, label, question, suppressContextMenu };

  // Tracks the currently-attached element + its listeners so we can
  // detach when React calls the callback with a new node or null.
  const attachedRef = useRef(null);

  // Stable callback ref — identity never changes, so we won't churn
  // mount/unmount cycles on the consuming element.
  return useCallback((el) => {
    // Detach from a previous element if there was one.
    const prev = attachedRef.current;
    if (prev) {
      prev.el.removeEventListener('mousedown', prev.mark, true);
      prev.el.removeEventListener('focusin', prev.mark);
      prev.el.removeEventListener('contextmenu', prev.onContextMenu);
      attachedRef.current = null;
    }
    if (!el) return;

    const mark = () => {
      const t = targetRef.current;
      _last = { slug: t.slug, label: t.label, question: t.question, at: Date.now() };
    };

    const onContextMenu = (e) => {
      mark();
      const t = targetRef.current;
      if (!t.suppressContextMenu || isTextEditable(e.target)) return;
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('helpTarget:open', {
        detail: { slug: t.slug, label: t.label, question: t.question },
      }));
    };

    el.addEventListener('mousedown', mark, true);
    el.addEventListener('focusin', mark);
    el.addEventListener('contextmenu', onContextMenu);
    attachedRef.current = { el, mark, onContextMenu };
  }, []);
}
